import { Outlet } from "@tanstack/react-router"
import { useCampaignQuery } from "modules/campaigns/hooks/useCampaignQuery"
import { ClassificationGoalInsights } from "modules/insights/components/ClassificationGoal"
import { NPSInsights } from "modules/insights/components/NPS"
import { OpenGoalInsights } from "modules/insights/components/OpenGoal"
import { PredefinedGoalInsights } from "modules/insights/components/PredefinedGoal"
import { Loading } from "modules/shared/components/Loading"
import { TimeRangeSelector } from "modules/shared/components/TimeRange/TimeRangeSelector"
import { useTranslation } from "react-i18next"
import { ChannelEnum, FlowEnum, GoalTypeEnum } from "schema"
import { useCampaignTimeFrame } from "../CampaignTimeFrameContext"
import { useCampaignViewFormat } from "../CampaignViewFormatContext"
import { ViewFormatSelector } from "../ViewFormatSelector"
import { CampaignDetailActivity } from "./Activity"
import { CampaignWhatsappTemplateInsights } from "./WhatsappTemplates"

type Props = {
  id: string
}

export const CampaignInsights = ({ id }: Props) => {
  const { data: campaign, isPending: isCampaignPending } = useCampaignQuery(id)
  const { t } = useTranslation()
  const { timeFrame, setTimeFrame } = useCampaignTimeFrame()
  const { viewFormat, setViewFormat } = useCampaignViewFormat()

  if (isCampaignPending || !campaign) return <Loading />

  const hasGoals = !!(
    campaign.goals &&
    campaign.goals.filter((goal) => goal.goal.type !== GoalTypeEnum.Informative).length > 0
  )

  return (
    <div className="flex flex-col gap-10">
      <CampaignDetailActivity campaignId={id} />
      {campaign.channel === ChannelEnum.Whatsapp && campaign.flow === FlowEnum.Outbound && (
        <CampaignWhatsappTemplateInsights campaign={campaign} />
      )}
      {hasGoals && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-base font-semibold">{t("campaigns.detail.goals")}</h3>
            <div className="flex gap-2">
              <ViewFormatSelector value={viewFormat} onChange={setViewFormat} />
              <TimeRangeSelector
                value={timeFrame}
                onChange={({ timeFrame }) => setTimeFrame(timeFrame)}
              />
            </div>
          </div>
          <div className="flex flex-col gap-4">
            {campaign.goals?.map((campaignGoal) => {
              switch (campaignGoal.goal.type) {
                case GoalTypeEnum.NPS:
                  return (
                    <NPSInsights
                      key={`nps-${campaignGoal.goal.id}`}
                      campaignId={campaign.id}
                      goal={campaignGoal.goal}
                    />
                  )
                case GoalTypeEnum.Open:
                  return (
                    <OpenGoalInsights
                      key={`open-${campaignGoal.goal.id}`}
                      campaignId={campaign.id}
                      goal={campaignGoal.goal}
                    />
                  )
                case GoalTypeEnum.Classification:
                  return (
                    <ClassificationGoalInsights
                      key={`classification-${campaignGoal.goal.id}`}
                      campaignId={campaign.id}
                      goal={campaignGoal.goal}
                    />
                  )
                case GoalTypeEnum.Predefined:
                  return (
                    <PredefinedGoalInsights
                      key={`predefined-${campaignGoal.goal.id}`}
                      campaignId={campaign.id}
                      goal={campaignGoal.goal}
                    />
                  )
                case GoalTypeEnum.Informative:
                  return null
              }
            })}
          </div>
        </div>
      )}
      <Outlet />
    </div>
  )
}
